import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import React, { useEffect, useState } from 'react'
import BrandFilter from './BrandFilter'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import {
    cancelGetAmbassadorList,
    getAmbassadorFilters,
    getAmbassadorList,
    getBrandContentTypes,
    saveAmbassadorList,
    selectBrandContentTypes,
    selectCheckIsContentTypeExist,
    selectFilteringParams,
    selectSearchTerm,
    selectSelectedSort,
    setFilterModel,
    setFilterOptions,
    setSortItem,
} from 'state/ambassadors'
import styled from 'particles/themes/styled'
import { TFunction } from 'i18next'
import { PLATFORM_CONTENT_TYPE_ENUM } from 'services/api/constants'
import _ from 'lodash'
import { selectRequestsAreLoading } from 'state'
import { setLoading, STATE_KEYS } from 'state/requests'

const getPlatformContentTypeLabels = (t: TFunction): Record<string, string> => {
    return {
        [PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_STORY]: t('taskType.instagramStory'),
        [PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_POST]: t('taskType.instagramPost'),
        [PLATFORM_CONTENT_TYPE_ENUM.INSTAGRAM_REEL]: t('taskType.instagramReels'),
        [PLATFORM_CONTENT_TYPE_ENUM.TIKTOK_POST]: t('taskType.tikTokVideo'),
        [PLATFORM_CONTENT_TYPE_ENUM.FACEBOOK_POST]: t('taskType.facebookPost'),
        [PLATFORM_CONTENT_TYPE_ENUM.TWITTER_POST]: t('taskType.twitter'),
        [PLATFORM_CONTENT_TYPE_ENUM.YOUTUBE_VIDEO]: t('taskType.youtube'),
        [PLATFORM_CONTENT_TYPE_ENUM.XIAOHONGSHU_POST]: t('taskType.xiaohongshuPost'),
    }
}

interface FiltersProps {
    contentType: PLATFORM_CONTENT_TYPE_ENUM | null
    setContentType: React.Dispatch<React.SetStateAction<PLATFORM_CONTENT_TYPE_ENUM | null>>
}

const Filters = ({ contentType, setContentType }: FiltersProps) => {
    const platformContentTypeData = useSelector(selectBrandContentTypes)
    const [t] = useNewTranslation()
    const [selectedBrands, setSelectedBrands] = useState<string[]>([])
    const dispatch = useDispatch()
    const filteringParams = useSelector(selectFilteringParams)
    const platformContentTypeLabel = getPlatformContentTypeLabels(t)
    const searchTerm = useSelector(selectSearchTerm)
    const sortQuery = useSelector(selectSelectedSort)
    const checkIsContentTypeExist = useSelector(selectCheckIsContentTypeExist)
    const isBrandContentTypeLoading = useSelector(
        selectRequestsAreLoading([STATE_KEYS.AmbassadorBrands, STATE_KEYS.BrandContentTypes]),
    )
    const isGetAmbassadorListLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.AmbassadorList]))

    const [memoizedFilteringParams, setMemoizedFilteringParams] = useState({
        ...filteringParams,
        keywords: searchTerm,
    })

    useEffect(() => {
        if (!_.isEqual(memoizedFilteringParams, { ...filteringParams, keywords: searchTerm })) {
            setMemoizedFilteringParams({
                ...filteringParams,
                keywords: searchTerm,
            })
        }
    }, [filteringParams, searchTerm])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (selectedBrands.length) {
                dispatch(
                    getBrandContentTypes({
                        brands: selectedBrands,
                    }),
                )
            } else {
                setContentType(null)
            }
        }, 300)

        return () => clearTimeout(timeout)
    }, [selectedBrands])

    useEffect(() => {
        // to reset the content type dropdown if the content type is not available in the platform content type data
        if (contentType && !checkIsContentTypeExist(contentType)) {
            setContentType(null)
            dispatch(cancelGetAmbassadorList())
            dispatch(saveAmbassadorList([]))
        }
    }, [platformContentTypeData, contentType])

    useEffect(() => {
        const shouldFetch =
            selectedBrands.length && contentType && checkIsContentTypeExist(contentType) && !isBrandContentTypeLoading

        const timeout = setTimeout(() => {
            if (shouldFetch) {
                dispatch(
                    getAmbassadorList({
                        brands: selectedBrands,
                        content_type: contentType,
                        filteringParams: memoizedFilteringParams,
                        ordering: sortQuery ?? undefined,
                    }),
                )
            }
        }, 300)

        return () => clearTimeout(timeout)
    }, [selectedBrands, contentType, memoizedFilteringParams, sortQuery, isBrandContentTypeLoading])

    useEffect(() => {
        if (selectedBrands.length && contentType) {
            dispatch(
                getAmbassadorFilters({
                    brands: selectedBrands,
                    content_type: contentType,
                }),
            )
        } else {
            // reset filter & sort
            dispatch(
                setFilterModel({
                    options: {
                        items: [],
                    },
                }),
            )
            dispatch(setFilterOptions([]))
            dispatch(setSortItem(null))
        }
    }, [selectedBrands, contentType])

    return (
        <FilterContainer>
            <div className="dropdown-filters">
                <BrandFilter
                    disabled={isGetAmbassadorListLoading}
                    selectedBrands={selectedBrands}
                    setSelectedBrands={setSelectedBrands}
                />

                <Dropdown
                    label={t('campaignEditor.customAndRegistrationField.taskTypeHeader')}
                    placeholder={t('manageAmbassandor.taskTypeSelectorPlaceholder')}
                    width={220}
                    isDisabled={isGetAmbassadorListLoading || selectedBrands.length === 0}
                    onChange={value => {
                        dispatch(saveAmbassadorList([]))
                        dispatch(
                            setLoading({
                                isLoading: true,
                                stateKey: STATE_KEYS.AmbassadorList,
                            }),
                        )
                        setContentType(value as PLATFORM_CONTENT_TYPE_ENUM)
                    }}
                    value={contentType}
                    dataTestId="ambassador-platform-dropdown"
                >
                    {!!platformContentTypeData.instagram?.length && <MenuItem.Title text="Instagram" />}

                    {platformContentTypeData.instagram?.map((value, index) => (
                        <MenuItem.Text
                            icon="instagramLogo"
                            key={`ig-dropdown-${index}`}
                            text={platformContentTypeLabel[value]}
                            value={value}
                            tabIndex={index}
                        />
                    ))}

                    {!!platformContentTypeData.facebook?.length && <MenuItem.Title text="Facebook" />}

                    {platformContentTypeData.facebook?.map((value, index) => (
                        <MenuItem.Text
                            icon="facebookLogo"
                            key={`fb-dropdown-${index}`}
                            value={value}
                            text={platformContentTypeLabel[value]}
                            tabIndex={index}
                        />
                    ))}

                    {!!platformContentTypeData.tiktok?.length && <MenuItem.Title text="Tiktok" />}

                    {platformContentTypeData.tiktok?.map((value, index) => (
                        <MenuItem.Text
                            icon="tiktokLogo"
                            key={`tiktok-dropdown-${index}`}
                            text={platformContentTypeLabel[value]}
                            value={value}
                            tabIndex={index}
                        />
                    ))}

                    {!!platformContentTypeData.youtube?.length && <MenuItem.Title text="Youtube" />}

                    {platformContentTypeData.youtube?.map((value, index) => (
                        <MenuItem.Text
                            icon="youtubeLogo"
                            key={`youtube-dropdown-${index}`}
                            text={platformContentTypeLabel[value]}
                            value={value}
                            tabIndex={index}
                        />
                    ))}

                    {!!platformContentTypeData.xiaohongshu?.length && <MenuItem.Title text="Xiaohongshu" />}

                    {platformContentTypeData.xiaohongshu?.map((value, index) => (
                        <MenuItem.Text
                            icon="xiaohongshuLogo"
                            key={`xiaohongshu-dropdown-${index}`}
                            text={platformContentTypeLabel[value]}
                            value={value}
                            tabIndex={index}
                        />
                    ))}
                </Dropdown>
            </div>
        </FilterContainer>
    )
}

export default Filters

const FilterContainer = styled.div`
    .dropdown-filters {
        display: flex;
        gap: 12px;
        align-items: center;
    }
`
